import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generic "data → PDF" used by the assistant's `create_pdf` tool. The tool hands
 * back { title?, subtitle?, columns?, rows } and this renders a tabular PDF and
 * triggers a download. Rows may be an array of objects or an array of arrays.
 */
export interface DataPdfPayload {
  title?: string;
  subtitle?: string;
  columns?: string[];
  rows?: Array<Record<string, unknown>> | unknown[][];
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function slug(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'report'
  );
}

export function generateDataPdf(payload: DataPdfPayload): void {
  const doc = new jsPDF({ orientation: 'landscape' });
  const title = payload.title?.trim() || 'Report';

  doc.setFontSize(16);
  doc.text(title, 14, 18);

  let y = 25;
  if (payload.subtitle) {
    doc.setFontSize(10);
    doc.setTextColor(110);
    doc.text(payload.subtitle, 14, y);
    doc.setTextColor(0);
    y += 6;
  }
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(`Generated ${new Date().toLocaleString()}`, 14, y);
  doc.setTextColor(0);
  y += 4;

  const rows = payload.rows ?? [];
  let head: string[][] = [];
  let body: string[][] = [];

  if (rows.length > 0 && !Array.isArray(rows[0])) {
    // Array of objects.
    const objRows = rows as Array<Record<string, unknown>>;
    const cols = payload.columns?.length ? payload.columns : Object.keys(objRows[0] ?? {});
    head = [cols];
    body = objRows.map((r) => cols.map((c) => formatCell(r[c])));
  } else {
    // Array of arrays.
    const arrRows = rows as unknown[][];
    if (payload.columns?.length) head = [payload.columns];
    body = arrRows.map((r) => r.map(formatCell));
  }

  autoTable(doc, {
    head,
    body,
    startY: y + 2,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [37, 99, 235] },
  });

  doc.save(`${slug(title)}.pdf`);
}
